/*  iceberg-ui  */
import { IconWithVariant } from '@auviknetworks/iceberg-ui';

/*  MUI  */
import { Box, Grid } from '@mui/material';
import { CellContext } from '@tanstack/react-table';
import { DarkTooltip, TooltipQ } from 'application/Shared/Components/Tooltips';
import { FontSizes } from 'application/Shared/Constants';

/* Shared */
import { Route } from 'application/Shared/Helpers';
import { ClientLink } from 'application/Shared/types/clientLink';

/*  React  */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

/* Internal */
import { ClientId } from './ClientId';
import { ClientName } from './ClientName';
import { usePartnerClientsTableContext } from './PartnerClientsTableContext';

const LearningModeIcon = () => {
    const { t } = useTranslation();

    return (
        <DarkTooltip
            arrow
            title={
                <div>
                    {t('Common.learning_mode')}
                    <br />
                    <br />
                    {t('Tooltips.Clients.learning_mode')}
                </div>
            }
        >
            <Box>
                <IconWithVariant
                    data-testid="learningModeIcon"
                    variant="primary"
                    src="FileSearch"
                    size={FontSizes[2]}
                />
            </Box>
        </DarkTooltip>
    );
};

interface DomainAlertProps {
    routeToClient: string;
}

const DomainAlert = ({ routeToClient }: DomainAlertProps) => {
    const { t } = useTranslation();

    return (
        <Link
            to={`${routeToClient}/domains`}
            data-trackid="partner_clientInfo_noDomains_routeTo"
        >
            <DarkTooltip arrow title={t('Tooltips.Clients.no_domains')}>
                <Box>
                    <IconWithVariant
                        data-testid="domainAlertIcon"
                        variant="warningDark"
                        src="WarningTriangleFilled"
                        size={FontSizes[2]}
                    />
                </Box>
            </DarkTooltip>
        </Link>
    );
};

export const ClientInfoCell = ({ row }: CellContext<ClientLink, unknown>) => {
    const { t } = useTranslation();
    const { hidden, tenant, learningMode, platformTenantId } = row.original;
    const { blocked, domains } = tenant || {};

    const noDomains = !domains || !domains.length;

    const routeToClient = Route.buildBaseAdminRoute({
        clientId: tenant?.id as string,
        partnerId: platformTenantId
    });

    const { internalId, reloadSessionPartnerTenantLinks } =
        usePartnerClientsTableContext();

    return (
        <Grid container data-trackid="partnerClientsDashboardRenderClient">
            <Grid
                item
                xs={12}
                data-trackid="partnerClientsDashboardRenderClientGridItem"
            >
                <Box
                    display="flex"
                    alignItems="flex-start"
                    justifyContent="space-between"
                    data-trackid="partnerClientsDashboardRenderClientFlexBox"
                >
                    <Box display="flex" flex={1} alignContent="flex-start">
                        <Box
                            style={{ marginRight: '4px' }}
                            data-testid="clientInfoCellIcons"
                        >
                            {noDomains && (
                                <DomainAlert routeToClient={routeToClient} />
                            )}

                            {learningMode && <LearningModeIcon />}
                        </Box>
                        <Box flexDirection="column" flex={1}>
                            <Box display="flex" alignItems="flex-start">
                                <ClientName
                                    internalId={internalId}
                                    reloadSessionPartnerTenantLinks={
                                        reloadSessionPartnerTenantLinks
                                    }
                                    routeToClient={routeToClient}
                                    row={row.original}
                                />
                            </Box>
                            <ClientId row={row.original} />
                        </Box>

                        <Box />
                    </Box>
                    <Box
                        style={{ marginLeft: '4px' }}
                        display="flex"
                        alignItems="center"
                    >
                        {blocked && (
                            <TooltipQ
                                text={t('Tooltips.activity_disabled')}
                                content={
                                    <IconWithVariant
                                        data-testid="blockedIcon"
                                        variant="primary"
                                        src="DisabledPlay"
                                        size={FontSizes[2]}
                                        wrapperProps={{
                                            marginLeft: '4px',
                                            marginRight: '4px'
                                        }}
                                    />
                                }
                            />
                        )}
                        {hidden && (
                            <TooltipQ
                                text={t('Tooltips.Clients.removedFromPanel')}
                                content={
                                    <IconWithVariant
                                        data-testid="hiddenIcon"
                                        variant="primary"
                                        src="ViewOff"
                                        size={FontSizes[2]}
                                        wrapperProps={{
                                            marginLeft: '4px',
                                            marginRight: '4px'
                                        }}
                                    />
                                }
                            />
                        )}
                    </Box>
                </Box>
            </Grid>
        </Grid>
    );
};
